import React, { useState } from 'react'
import { X, AlertTriangle, Trash2 } from 'lucide-react'
import { employeeShiftService } from '../../services/employeeShiftService'
import InlineLoader from '../ui/InlineLoader'

const DeleteEmployeeShiftModal = ({ employeeShift, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Helper function to safely get property value
  const safeGet = (obj, path, defaultValue = 'N/A') => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Helper function to get branch name - handles both populated and non-populated data
  const getBranchName = (employee) => {
    if (!employee) return 'N/A'
    if (typeof employee.branch === 'object' && employee.branch?.name) {
      return employee.branch.name
    }
    return employee.branch || 'N/A'
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString()
  }

  // Handle deletion
  const handleDelete = async () => {
    try {
      setLoading(true)
      setError(null)
      
      await employeeShiftService.deleteEmployeeShift(employeeShift._id)
      onSuccess()
    } catch (err) {
      console.error('Error deleting employee shift:', err)
      setError(err.response?.data?.message || 'Failed to delete employee shift')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <AlertTriangle className="h-6 w-6 text-red-500 mr-2" />
            Delete Employee Shift
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Warning Message */}
        <div className="mb-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="text-red-800 font-medium mb-1">Warning</h3>
                <p className="text-red-700 text-sm">
                  This action cannot be undone. The employee shift assignment will be permanently deleted.
                </p>
              </div>
            </div>
          </div>

          {/* Shift Details */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">Shift Details to be deleted:</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Employee:</span>
                <span className="font-medium text-gray-900">
                  {safeGet(employeeShift, 'employee.fullName')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Shift:</span>
                <span className="font-medium text-gray-900">
                  {safeGet(employeeShift, 'shift.shiftName')}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Branch:</span>
                <span className="font-medium text-gray-900">
                  {getBranchName(employeeShift.employee)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Start Date:</span>
                <span className="font-medium text-gray-900">
                  {formatDate(employeeShift.startDate)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`font-medium ${
                  employeeShift.status === 'open' ? 'text-green-600' :
                  employeeShift.status === 'close' ? 'text-red-600' :
                  employeeShift.status === 'pending' ? 'text-yellow-600' :
                  'text-gray-600'
                }`}>
                  {employeeShift.status || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={loading}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 flex items-center space-x-2"
          >
            {loading ? (
              <InlineLoader size="sm" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            <span>Delete Shift</span>
          </button>
        </div>
      </div>
    </div>
  )
}

export default DeleteEmployeeShiftModal
